import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetBlogsQuery } from '../../store/slices/apiSlice';
import { ArrowLeft, Edit, User, Tag, Calendar, Clock, Star, Eye, MessageCircle, ThumbsUp } from 'lucide-react';
import toast from 'react-hot-toast';

const ViewBlog: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: blogsData, isLoading: blogsLoading } = useGetBlogsQuery();
  const [blog, setBlog] = useState<any>(null);

  useEffect(() => {
    if (blogsData?.data?.blogs && id) {
      const foundBlog = blogsData.data.blogs.find((b: any) => b.id === parseInt(id));
      if (foundBlog) {
        setBlog(foundBlog);
      } else {
        toast.error('Blog not found');
        navigate('/admin/blogs');
      }
    }
  }, [blogsData, id, navigate]);

  if (blogsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Blog not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">View Blog</h1>
            <p className="text-gray-600 mt-1">Blog details and content</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/admin/blogs/edit/${id}`)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit className="w-5 h-5 mr-2" />
          Edit Blog
        </button>
      </div>

      {/* Blog Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Blog Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{blog.title}</h1>
          <p className="text-lg text-gray-600 mb-4">{blog.desc}</p>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-1" />
              <span>By {blog.author}</span>
            </div>
            <div className="flex items-center">
              <Tag className="w-4 h-4 mr-1" />
              <span>{blog.category}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{formatDateUtil(blog.publishedAt)}</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              <span>{blog.readTime} min read</span>
            </div>
            <span className={`px-3 py-1 text-xs rounded-full ${
              blog.status === 'published' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {blog.status}
            </span>
            {blog.featured && (
              <div className="flex items-center text-yellow-600">
                <Star className="w-4 h-4 mr-1 fill-current" />
                <span>Featured</span>
              </div>
            )}
          </div>
        </div>

        {/* Blog Image */}
        {blog.image && (
          <div className="w-full">
            <img 
              src={blog.image} 
              alt={blog.title}
              className="w-full h-96 object-cover"
            />
          </div>
        )}

        {/* Blog Content */}
        <div className="p-6">
          <div 
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>

        {/* Blog Stats */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Blog Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{blog.views}</div>
              <div className="text-sm text-gray-500">Views</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <ThumbsUp className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{blog.likes}</div>
              <div className="text-sm text-gray-500">Likes</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <MessageCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{blog.comments}</div>
              <div className="text-sm text-gray-500">Comments</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Star className="w-5 h-5 text-orange-600 fill-current" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{blog.rating}</div>
              <div className="text-sm text-gray-500">Rating</div>
            </div>
          </div>
        </div>

        {/* Tags and Keywords */}
        <div className="p-6 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {blog.tags && blog.tags.map((tag: string, index: number) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">SEO Keywords</h3>
              <div className="flex flex-wrap gap-2">
                {blog.seoKeywords && blog.seoKeywords.map((keyword: string, index: number) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Meta Information */}
        <div className="p-6 bg-gray-50 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Meta Information</h3>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-600">Meta Title:</span>
              <p className="text-sm text-gray-900 mt-1">{blog.metaTitle || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Meta Description:</span>
              <p className="text-sm text-gray-900 mt-1">{blog.metaDescription || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Slug:</span>
              <p className="text-sm text-gray-900 font-mono mt-1">{blog.slug}</p>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="p-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Created:</span>
              <p className="text-gray-900 mt-1">{formatDateTimeUtil(blog.created_at)}</p>
            </div>
            <div>
              <span className="text-gray-600">Updated:</span>
              <p className="text-gray-900 mt-1">{formatDateTimeUtil(blog.updated_at)}</p>
            </div>
            <div>
              <span className="text-gray-600">Published:</span>
              <p className="text-gray-900 mt-1">{formatDateTimeUtil(blog.publishedAt)}</p>
            </div>
            <div>
              <span className="text-gray-600">Published By:</span>
              <p className="text-gray-900 mt-1">{blog.publishedBy}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBlog;





